import { useEffect, useState, useCallback, useRef, cloneElement, isValidElement } from "react";
import { useMsal } from "@azure/msal-react";
import { FolderPlus, Folder, Pencil, Trash2 } from "lucide-react";
import { callApi } from "../api/api";
import RBAC from "../components/RBAC";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import Pagination from "../components/Pagination";

const RESOURCE_CODE_PATTERN = /^[A-Z0-9_]+$/;

const normalizeResourceCode = (value) => (value || "").trim().toUpperCase();

const RESOURCE_CODE_HINT =
  "Required. Use letters, numbers, and underscores only (e.g. SEARCH_DOC).";

const RESOURCE_CODE_TITLE =
  "Unique resource code. Allowed characters: A-Z, 0-9, and underscore (_).";

export default function Resources() {
  const { instance, accounts } = useMsal();


  const [resources, setResources] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selected, setSelected] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [form, setForm] = useState({
    resource_code: "",
    resource_name: "",
    resource_type_code: "",
    resource_path: "",
    access_level_code: "READ",
  });

  /* ================= SEARCH DEBOUNCE ================= */

  const [isNarrowToolbar, setIsNarrowToolbar] = useState(false);
  const [isAddTooltipVisible, setIsAddTooltipVisible] = useState(false);
  const addTooltipTouchTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (addTooltipTouchTimer.current) {
        clearTimeout(addTooltipTouchTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsNarrowToolbar(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  /* ================= LOAD ================= */

  const loadResources = useCallback(
    async (searchText = "", currentPage = 1) => {
      try {
        setLoading(true);

        const finalSearch =
          searchText !== undefined && searchText !== null ? searchText : "";

        const res = await callApi(
          instance,
          accounts,
          "GET",
          `/api/admin/resources?page=${currentPage}&pageSize=${pageSize}&search=${encodeURIComponent(finalSearch)}`,
        );

        const response = res?.data || {};

        setResources(response.data || []);
        setTotalRecords(response.total || 0);
      } catch (err) {
        console.error(err);

        setResources([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [instance, accounts, pageSize],
  );

  useEffect(() => {
    if (accounts.length) {
      loadResources(debouncedSearch, page);
    }
  }, [accounts.length, debouncedSearch, page, loadResources]);

  /* ================= HELPERS ================= */

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });

    setTimeout(() => setToast(null), 3000);
  };

  const resetForm = () => {
    setForm({
      resource_code: "",
      resource_name: "",
      resource_type_code: "",
      resource_path: "",
      access_level_code: "READ",
    });

    setSubmitAttempted(false);
  };

  const getResourceCodeError = () => {
    const raw = form.resource_code || "";
    const code = normalizeResourceCode(raw);

    if (raw && !RESOURCE_CODE_PATTERN.test(code)) {
      return "Code must use letters, numbers, and underscores only";
    }

    if (submitAttempted && !code) {
      return "Code required";
    }

    return null;
  };

  const getResourceNameError = () => {
    if (submitAttempted && !form.resource_name?.trim()) {
      return "Name required";
    }

    return null;
  };

  const getResourcePathError = () => {
    if (submitAttempted && !form.resource_path?.trim()) {
      return "Path required";
    }

    return null;
  };

  const validate = () => {
    setSubmitAttempted(true);

    const code = normalizeResourceCode(form.resource_code);

    return (
      !!code &&
      RESOURCE_CODE_PATTERN.test(code) &&
      !!form.resource_name?.trim() &&
      !!form.resource_path?.trim()
    );
  };

  const cleanPayload = () => ({
    resource_code: normalizeResourceCode(form.resource_code),
    resource_name: form.resource_name.trim(),
    resource_type_code: form.resource_type_code,
    resource_path: form.resource_path.trim(),
    access_level_code: form.access_level_code,
  });

  /* ================= ACTIONS ================= */

  const handleAdd = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "POST",
        "/api/admin/resources",
        cleanPayload(),
      );

      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Create failed");
      }

      showToast("Resource created");

      setShowAdd(false);

      resetForm();

      loadResources(debouncedSearch, page);
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Create failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selected) return;
    if (!validate()) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "PUT",
        `/api/admin/resources/${selected.resource_id}`,
        cleanPayload(),
      );

      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Update failed");
      }

      showToast("Resource updated");

      setShowEdit(false);

      setSelected(null);

      resetForm();

      loadResources(debouncedSearch, page);
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Update failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;

    try {
      setSubmitting(true);

      const res = await callApi(
        instance,
        accounts,
        "DELETE",
        `/api/admin/resources/${selected.resource_id}`,
      );

      if (!res || res?.data?.success === false) {
        throw new Error(res?.data?.message || "Delete failed");
      }

      showToast("Resource deleted");

      setShowDelete(false);

      setSelected(null);

      if (resources.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        loadResources(debouncedSearch, page);
      }
    } catch (err) {
      showToast(
        err?.response?.data?.message || err.message || "Delete failed",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openAddResourceModal = () => {
    resetForm();
    setShowAdd(true);
  };

  const openEditResourceModal = useCallback((resource) => {
    setSelected(resource);
    setForm({
      resource_code: resource.resource_code,
      resource_name: resource.resource_name,
      resource_type_code: resource.resource_type_code || "",
      resource_path: resource.resource_path,
      access_level_code: resource.access_level_code,
    });
    setSubmitAttempted(false);
    setShowEdit(true);
  }, []);

  const showAddTooltip = () => setIsAddTooltipVisible(true);

  const hideAddTooltip = () => setIsAddTooltipVisible(false);

  const handleAddTooltipTouchStart = () => {
    if (addTooltipTouchTimer.current) {
      clearTimeout(addTooltipTouchTimer.current);
    }
    setIsAddTooltipVisible(true);
  };

  const handleAddTooltipTouchEnd = () => {
    addTooltipTouchTimer.current = setTimeout(() => {
      setIsAddTooltipVisible(false);
    }, 1200);
  };

  /* ================= RESPONSIVE CARD MAPPERS ================= */

  const mapResourceCard = useCallback(
    (r) => ({
      id: `resource-${r.resource_id}`,
      icon: <Folder size={16} />,
      tone: r.access_level_code === "WRITE" ? "success" : "info",
      title: r.resource_name,
      subtitle: r.resource_code || "—",
      meta: [
        { label: "Code", value: r.resource_code || "—" },
        { label: "Type", value: r.resource_type_code || "—" },
        { label: "Access", value: r.access_level_code || "—" },
        { label: "Path", value: r.resource_path || "—" },
      ],
      badges: [
        {
          label: "Access",
          value: r.access_level_code || "—",
          tone: r.access_level_code === "WRITE" ? "success" : "info",
        },
      ],
      actions: [
        {
          label: "Edit",
          icon: <Pencil size={14} />,
          onClick: () => openEditResourceModal(r),
        },
        {
          label: "Delete",
          icon: <Trash2 size={14} />,
          variant: "secondary",
          onClick: () => {
            setSelected(r);
            setShowDelete(true);
          },
        },
      ],
    }),
    [openEditResourceModal],
  );

  const mapResourceDrawer = useCallback(
    (r) => ({
      title: r.resource_name,
      fields: [
        { label: "Resource Code", value: r.resource_code || "—" },
        { label: "Type Code", value: r.resource_type_code || "—" },
        { label: "Access Level", value: r.access_level_code || "—" },
        { label: "Path", value: r.resource_path || "—" },
      ],
      primaryAction: {
        label: "Edit Resource",
        onClick: () => openEditResourceModal(r),
      },
    }),
    [openEditResourceModal],
  );

  const renderTable = () => (
    <>
      <div style={styles.tableCard}>
        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : (
          <ScrollableTable>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>NAME</th>

                <th style={styles.th}>CODE</th>

                <th style={styles.th}>TYPE</th>

                <th style={styles.th}>ACCESS</th>

                <th style={styles.th}>PATH</th>

                <th style={styles.thRight}>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {resources.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      padding: "32px",
                      textAlign: "center",
                      color: "#64748b",
                      fontSize: "14px",
                    }}
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                resources.map((r) => (
                  <tr
                    key={r.resource_id}
                    style={styles.row}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f8fafc")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fff")
                    }
                  >
                    <td style={styles.td}>
                      <div
                        style={{
                          fontWeight: "600",
                        }}
                      >
                        {r.resource_name}
                      </div>
                    </td>

                    <td style={styles.td}>
                      <span style={styles.codeBadge}>
                        {r.resource_code || "—"}
                      </span>
                    </td>

                    <td style={styles.td}>
                      <span style={styles.typeBadge}>
                        {r.resource_type_code || "—"}
                      </span>
                    </td>

                    <td style={styles.td}>
                      <span
                        style={
                          r.access_level_code === "WRITE"
                            ? styles.accessWrite
                            : styles.accessRead
                        }
                      >
                        {r.access_level_code}
                      </span>
                    </td>

                    <td
                      style={{
                        ...styles.td,
                        minWidth: "320px",
                      }}
                    >
                      {r.resource_path || "—"}
                    </td>

                    <td style={styles.tdRight}>
                      <RBAC resource="ADMIN" access="WRITE">
                        <>
                          <span
                            style={styles.icon}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.transform = "scale(1.1)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                            onClick={() => openEditResourceModal(r)}
                          >
                            ✏️
                          </span>

                          <span
                            style={{
                              ...styles.icon,
                              color: "#ef4444",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.transform = "scale(1.1)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                            onClick={() => {
                              setSelected(r);
                              setShowDelete(true);
                            }}
                          >
                            🗑️
                          </span>
                        </>
                      </RBAC>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </ScrollableTable>
        )}
      </div>

      {totalRecords > 0 && (
        <Pagination
          page={page}
          totalPages={Math.ceil(totalRecords / pageSize) || 1}
          totalRecords={totalRecords}
          pageSize={pageSize}
          currentPageCount={resources.length}
          onPageChange={setPage}
        />
      )}
    </>
  );

  /* ================= UI ================= */

  const paginationProps =
    totalRecords > 0
      ? {
          page,
          totalPages: Math.ceil(totalRecords / pageSize) || 1,
          totalRecords,
          pageSize,
          currentPageCount: resources.length,
          onPageChange: setPage,
        }
      : null;

  return (
    <div className="page-container" style={styles.container}>
      <div
        style={{
          ...styles.header,
          ...(isNarrowToolbar ? styles.headerNarrow : null),
        }}
      >
        <div
          style={isNarrowToolbar ? styles.headerTitleBlock : undefined}
        >
          <h2 style={styles.title}>Resources</h2>

          <p style={styles.sub}>Manage RBAC resources</p>
        </div>

        <RBAC resource="ADMIN" access="WRITE">
          {isNarrowToolbar ? (
            <div style={styles.addBtnWrap}>
              <button
                type="button"
                style={{
                  ...styles.addBtn,
                  ...styles.addBtnCompact,
                }}
                aria-label="Add resource"
                aria-describedby={
                  isAddTooltipVisible ? "resources-add-tooltip" : undefined
                }
                onMouseEnter={showAddTooltip}
                onMouseLeave={hideAddTooltip}
                onFocus={showAddTooltip}
                onBlur={hideAddTooltip}
                onTouchStart={handleAddTooltipTouchStart}
                onTouchEnd={handleAddTooltipTouchEnd}
                onClick={openAddResourceModal}
              >
                <FolderPlus size={20} strokeWidth={2} aria-hidden />
              </button>
              {isAddTooltipVisible && (
                <div
                  id="resources-add-tooltip"
                  style={styles.addTooltip}
                  role="tooltip"
                >
                  <span style={styles.addTooltipArrow} aria-hidden />
                  Add resource
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              style={styles.addBtn}
              onClick={openAddResourceModal}
            >
              + Add resource
            </button>
          )}
        </RBAC>
      </div>

      <div
        style={{
          ...styles.toolbar,
          ...(isNarrowToolbar ? styles.toolbarNarrow : null),
        }}
      >
        <input
          placeholder="Search resources"
          style={{
            ...styles.search,
            ...(isNarrowToolbar ? styles.searchNarrow : null),
          }}
          value={search}
          onFocus={(e) => (e.target.style.border = "1px solid #2563eb")}
          onBlur={(e) => (e.target.style.border = "1px solid #dbe2ea")}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />

      </div>

      <ResponsiveTableCards
        breakpoint={1024}
        data={resources}
        loading={loading}
        emptyTitle="No Resources Found"
        emptySub="No resources match your current search."
        listTitle="Resources"
        listSubtitle="Manage RBAC resources"
        cardMapper={mapResourceCard}
        drawerMapper={mapResourceDrawer}
        pagination={paginationProps || undefined}
        renderTable={renderTable}
      />

      {(showAdd || showEdit) && (
        <Modal
          title={showAdd ? "Add Resource" : "Edit Resource"}
          onClose={() => {
            setShowAdd(false);
            setShowEdit(false);
            resetForm();
          }}
        >
          <FormField
            label="Code"
            required
            hint={RESOURCE_CODE_HINT}
            hintOnHover
            error={getResourceCodeError()}
          >
            <Input
              value={form.resource_code}
              placeholder="e.g. SEARCH_DOC"
              title={RESOURCE_CODE_TITLE}
              invalid={!!getResourceCodeError()}
              onChange={(v) => setForm({ ...form, resource_code: v })}
            />
          </FormField>

          <FormField
            label="Name"
            required
            error={getResourceNameError()}
          >
            <Input
              value={form.resource_name}
              invalid={!!getResourceNameError()}
              onChange={(v) => setForm({ ...form, resource_name: v })}
            />
          </FormField>

          <div style={styles.field}>
            <label style={styles.label}>Access Level</label>

            <select
              style={styles.input}
              value={form.access_level_code}
              onChange={(e) =>
                setForm({
                  ...form,
                  access_level_code: e.target.value,
                })
              }
            >
              <option value="READ">READ</option>

              <option value="WRITE">WRITE</option>
            </select>
          </div>

          <FormField
            label="Path"
            required
            error={getResourcePathError()}
          >
            <Input
              value={form.resource_path}
              invalid={!!getResourcePathError()}
              onChange={(v) => setForm({ ...form, resource_path: v })}
            />
          </FormField>

          <div style={styles.footer}>
            <button
              style={styles.cancelBtn}
              onClick={() => {
                setShowAdd(false);
                setShowEdit(false);
              }}
            >
              Cancel
            </button>

            <button
              style={styles.primaryBtn}
              disabled={submitting}
              onClick={showAdd ? handleAdd : handleEdit}
            >
              {submitting ? "Saving..." : showAdd ? "Create" : "Update"}
            </button>
          </div>
        </Modal>
      )}

      {showDelete && (
        <Modal title="Delete Resource" onClose={() => setShowDelete(false)}>
          <p>Are you sure?</p>

          <button style={styles.dangerBtn} onClick={handleDelete}>
            Delete
          </button>
        </Modal>
      )}

      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "error" ? "#ef4444" : "#16a34a",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
/* COMPONENTS */

const FormField = ({ label, required, hint, hintOnHover, error, children }) => {
  const [hintVisible, setHintVisible] = useState(false);
  const showGuidance = hintOnHover && hintVisible && !error;

  let fieldControl = children;
  if (hintOnHover && isValidElement(children)) {
    fieldControl = cloneElement(children, {
      onFocus: (...args) => {
        setHintVisible(true);
        children.props.onFocus?.(...args);
      },
      onBlur: (...args) => {
        setHintVisible(false);
        children.props.onBlur?.(...args);
      },
    });
  }

  return (
    <div
      style={styles.field}
      onMouseEnter={() => hintOnHover && setHintVisible(true)}
      onMouseLeave={() => hintOnHover && setHintVisible(false)}
    >
      <label style={styles.label}>
        {label}
        {required ? <span style={styles.requiredMark}> *</span> : null}
      </label>

      {fieldControl}

      {error ? (
        <Error msg={error} />
      ) : showGuidance ? (
        <p style={styles.hint}>{hint}</p>
      ) : null}
    </div>
  );
};

const Input = ({
  value,
  onChange,
  onBlur,
  onFocus,
  title,
  invalid,
  placeholder,
}) => (
  <input
    style={{
      ...styles.input,
      ...(invalid ? styles.inputInvalid : null),
    }}
    value={value}
    placeholder={placeholder}
    title={title}
    onChange={(e) => onChange(e.target.value)}
    onFocus={onFocus}
    onBlur={onBlur}
  />
);

const Error = ({ msg }) => <div style={styles.error}>{msg}</div>;

const Modal = ({ title, children, onClose }) => (
  <div style={styles.overlay}>
    <div style={styles.modal}>
      <div style={styles.modalHeader}>
        <h3>{title}</h3>

        <span style={styles.close} onClick={onClose}>
          ✕
        </span>
      </div>

      {children}
    </div>
  </div>
);

const styles = {
  container: {
    background: "#f8fafc",
    minHeight: "80vh",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    gap: "20px",
    flexWrap: "wrap",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "8px",
    fontSize: "14px",
  },

  addBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "10px 16px",
    fontWeight: "600",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    flexShrink: 0,
  },

  headerNarrow: {
    flexWrap: "nowrap",
    alignItems: "flex-start",
  },

  headerTitleBlock: {
    flex: "1 1 0",
    minWidth: 0,
  },

  addBtnCompact: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px",
    minWidth: "40px",
    minHeight: "40px",
  },

  addBtnWrap: {
    position: "relative",
    flexShrink: 0,
  },

  addTooltip: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    background: "#0f172a",
    color: "#f8fafc",
    border: "1px solid #1e293b",
    borderRadius: "8px",
    padding: "6px 10px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.16)",
    zIndex: 20,
    pointerEvents: "none",
  },

  addTooltipArrow: {
    position: "absolute",
    top: "-6px",
    right: "14px",
    width: 0,
    height: 0,
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    borderBottom: "6px solid #0f172a",
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
    flexWrap: "nowrap",
    background: "#fff",
    padding: "16px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },

  search: {
    flex: "1 1 0",
    minWidth: 0,
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    outline: "none",
    transition: "all 0.2s ease",
    background: "#fff",
  },

  count: {
    fontSize: "13px",
    color: "#64748b",
    marginLeft: "auto",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  toolbarNarrow: {
    flexWrap: "wrap",
  },

  searchNarrow: {
    flex: "1 1 100%",
    width: "100%",
  },

  countNarrow: {
    flex: "1 1 100%",
    width: "100%",
    marginLeft: 0,
    textAlign: "center",
  },

  tableCard: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  th: {
    padding: "16px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  thRight: {
    padding: "16px",
    textAlign: "right",
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  row: {
    borderBottom: "1px solid #f1f5f9",
  },

  td: {
    padding: "16px",
    fontSize: "14px",
    color: "#0f172a",
  },

  tdRight: {
    padding: "16px",
    textAlign: "right",
  },

  codeBadge: {
    display: "inline-block",
    background: "#f1f5f9",
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "12px",
  },

  typeBadge: {
    display: "inline-flex",
    alignItems: "center",
    background: "#e2e8f0",
    color: "#334155",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  accessRead: {
    display: "inline-flex",
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  accessWrite: {
    display: "inline-flex",
    background: "#dcfce7",
    color: "#15803d",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  icon: {
    cursor: "pointer",
    marginLeft: "14px",
    fontSize: "16px",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },

  modal: {
    background: "#fff",
    borderRadius: "18px",
    width: "100%",
    maxWidth: "520px",
    padding: "24px",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },

  close: {
    cursor: "pointer",
    fontSize: "18px",
    color: "#64748b",
  },

  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#334155",
    marginBottom: "6px",
    marginTop: "14px",
  },

  requiredMark: {
    color: "#dc2626",
    fontWeight: "700",
  },

  field: {
    marginBottom: "4px",
  },

  hint: {
    color: "#64748b",
    fontSize: "12px",
    marginTop: "6px",
    marginBottom: 0,
    lineHeight: 1.45,
  },

  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    boxSizing: "border-box",
    outline: "none",
  },

  inputInvalid: {
    border: "1px solid #dc2626",
    background: "#fef2f2",
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "28px",
  },

  cancelBtn: {
    border: "1px solid #dbe2ea",
    background: "#fff",
    padding: "10px 16px",
    borderRadius: "10px",
    cursor: "pointer",
  },

  primaryBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
  },

  dangerBtn: {
    background: "#dc2626",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
    marginTop: "20px",
  },

  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    color: "#fff",
    padding: "14px 18px",
    borderRadius: "12px",
    fontWeight: "500",
  },

  error: {
    color: "#dc2626",
    fontSize: "12px",
    marginTop: "6px",
  },

  empty: {
    padding: "40px",
    textAlign: "center",
    color: "#64748b",
  },

  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    borderTop: "1px solid #e2e8f0",
  },

  pageBtn: {
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1px solid #dbe2ea",
    background: "#fff",
    fontWeight: "500",
  },

  pageText: {
    fontSize: "14px",
    color: "#475569",
  },
};
